import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { MeUser } from '@/types/api'
import { api } from '@/lib/api'
import { storage } from '@/lib/storage'

type AuthState = {
  user: MeUser | null
  ready: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeUser | null>(null)
  const [ready, setReady] = useState(false)

  const signOut = useCallback(() => {
    storage.clearSession()
    setUser(null)
    setReady(true)
  }, [])

  useEffect(() => {
    let alive = true
    ;(async () => {
      if (!storage.accessToken()) {
        if (alive) setReady(true)
        return
      }
      try {
        const me = await api.me()
        if (alive) {
          setUser(me)
          storage.setUserJson(JSON.stringify(me))
        }
      } catch {
        if (alive) {
          storage.clearSession()
          setUser(null)
        }
      } finally {
        if (alive) setReady(true)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  const refreshUser = useCallback(async () => {
    if (!storage.accessToken()) {
      setUser(null)
      return
    }
    const me = await api.me()
    setUser(me)
    storage.setUserJson(JSON.stringify(me))
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const tokens = await api.login({ email, password })
    storage.setAccessToken(tokens.access_token)
    storage.setRefreshToken(tokens.refresh_token)
    const me = await api.me()
    setUser(me)
    storage.setUserJson(JSON.stringify(me))
    setReady(true)
  }, [])

  const value = useMemo(
    () => ({ user, ready, signIn, signOut, refreshUser }),
    [user, ready, signIn, signOut, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}
